import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import type { Issue } from '../../api/types';
import type { IssueSearch } from './useIssueSearch';
import {
  useIssuePickerStyles,
  STATE_CATEGORY_COLORS,
} from './useIssuePickerStyles';

type SearchProps = { search: IssueSearch; onSelect: (issue: Issue) => void };

function IssueResult({
  item,
  onSelect,
}: {
  item: Issue;
  onSelect: (issue: Issue) => void;
}) {
  const classes = useIssuePickerStyles();
  return (
    <ListItem button onClick={() => onSelect(item)}>
      <ListItemText
        secondaryTypographyProps={{ component: 'div' }}
        primary={`${item.type ? `${item.type} ` : ''}#${item.externalId} — ${
          item.title
        }`}
        secondary={
          <>
            <Chip
              size="small"
              label={item.state}
              color={STATE_CATEGORY_COLORS[item.stateCategory] ?? 'default'}
            />
            {item.assignee && (
              <span className={classes.assignee}>{item.assignee}</span>
            )}
          </>
        }
      />
      <ListItemSecondaryAction>
        <IconButton
          edge="end"
          aria-label="select"
          onClick={() => onSelect(item)}
        >
          <CheckIcon />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  );
}
function SearchFeedback({ search }: { search: IssueSearch }) {
  const classes = useIssuePickerStyles();
  if (search.search.error)
    return (
      <Typography
        color="error"
        variant="body2"
        data-testid="issue-picker-error"
      >
        {search.search.error}
      </Typography>
    );
  if (search.search.status !== 'loading' && search.search.items.length === 0)
    return (
      <div className={classes.empty}>
        <Typography variant="body2">
          Nenhuma issue encontrada com esses filtros.
        </Typography>
      </div>
    );
  return null;
}
function SearchContent({ search, onSelect }: SearchProps) {
  const classes = useIssuePickerStyles();
  return (
    <DialogContent>
      <div className={classes.searchRow}>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar por título ou ID..."
          value={search.query}
          onChange={ev => search.setQuery(ev.target.value)}
          inputProps={{ 'data-testid': 'issue-picker-search' }}
        />
        {search.search.status === 'loading' && <CircularProgress size={20} />}
      </div>
      <SearchFeedback search={search} />
      <List dense data-testid="issue-picker-list">
        {search.search.items.map(item => (
          <IssueResult
            key={`${item.providerId}-${item.externalId}`}
            item={item}
            onSelect={onSelect}
          />
        ))}
      </List>
    </DialogContent>
  );
}
export function IssueSearchDialog(props: SearchProps) {
  const close = () => props.search.setOpen(false);
  return (
    <Dialog open={props.search.open} onClose={close} fullWidth maxWidth="md">
      <DialogTitle>Selecionar Issue</DialogTitle>
      <SearchContent {...props} />
      <DialogActions>
        <Button onClick={close}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
}
